import { Link } from 'react-router-dom';
import { Section } from '../components/Section';
import { readConsent, writeConsent } from '../lib/consent';
import { clockTime } from '../lib/format';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { SEO } from '../seo/pages';

const h = 'mt-8 text-2xl';
const p = 'mt-3 text-ink-200';
const link = 'text-pink-300 underline hover:text-pink-200';

export function TermsPage() {
  usePageMeta(SEO.terms.title, SEO.terms.description);
  const { data: s } = useSettings();
  return (
    <Section narrow eyebrow="The fine print" title="Terms & no-refund policy">
      <h2 className={h}>All sales are final</h2>
      <p className={p}>
        Room reservations made on this site are paid in full at the time of booking and are{' '}
        <strong>non-refundable and non-transferable</strong>. Dates cannot be changed after booking.
        Donations made with an RSVP are gifts to Shawna and are{' '}
        <strong>final and non-refundable</strong>. If you are unsure of your plans, please wait to
        book until you are certain.
      </p>
      <h2 className={h}>What you are buying</h2>
      <p className={p}>
        A reservation is for a room type (not a specific room number or bed layout) at{' '}
        {s?.hotelName ?? 'the host hotel'} for the nights shown on your confirmation. The hotel
        assigns the room at check-in
        {s ? ` (check-in ${clockTime(s.checkInTime)}, check-out ${clockTime(s.checkOutTime)})` : ''}. The price shown is
        the whole price: no taxes or fees are added. Incidentals, parking of oversized vehicles and
        anything you charge to the room are between you and the hotel. A room is the RSVP for the
        people staying in it.
      </p>
      <p className={p}>
        An RSVP puts the named people on the list at the door and nothing else: no room, no
        breakfast. It is free. Any donation you add is a gift to Shawna, in the amount you
        choose, and buys nothing.
      </p>
      <h2 className={h}>Payment and who is charging you</h2>
      <p className={p}>
        Payments are processed by MobiusPay. Your card details go straight into the payment
        processor&apos;s secure form and are never seen or stored by this site; we keep only the
        card brand and last four digits with your reservation.
      </p>
      <p className={p}>
        The charge appears on your card statement as <strong>Icandy Enterprises</strong>, which
        collects room payments and RSVP donations on behalf of Brandon&apos;s family.
        {s?.contactEmail ? ` Questions about a charge: ${s.contactEmail}.` : ''}
      </p>
      <h2 className={h}>If something goes wrong</h2>
      <p className={p}>
        If the hotel cannot honour a confirmed reservation, you will be offered an equivalent room
        or a full refund of what you paid on this site; that is the only case in which a refund is
        issued. If you dispute a charge with your bank instead of contacting us first, the
        reservation is cancelled.
      </p>
      <h2 className={h}>Governing law</h2>
      <p className={p}>
        These terms are governed by the laws of the State of Florida, where the hotel is located and
        the organisers are based.
      </p>
      <h2 className={h}>Questions</h2>
      <p className={p}>
        {s?.contactEmail ? `Email ${s.contactEmail}, or use the ` : 'Use the '}
        <Link to="/contact" className={link}>
          contact page
        </Link>
        .
      </p>
    </Section>
  );
}

export function PrivacyPage() {
  usePageMeta(SEO.privacy.title, SEO.privacy.description);
  const { data: s } = useSettings();
  const resetConsent = () => {
    writeConsent(null);
    window.location.reload();
  };
  return (
    <Section narrow eyebrow="Your information" title="Privacy">
      <h2 className={h}>Who runs this site</h2>
      <p className={p}>
        BrandonStrong.com is operated by Icandy Enterprises, Lakeland, Florida, on behalf of
        Brandon&apos;s family. It exists for one event and collects only what is needed to hold
        your room or put your party on the door list.
      </p>
      <h2 className={h}>What we collect and why</h2>
      <p className={p}>
        When you reserve a room we keep your name, email, phone (optional), billing address, the
        brand and last four digits of your card, your reservation details, and the IP address and
        browser of the request (to stop fraud and bots). We use them to hold your room, email your
        confirmation, give the hotel its guest list, and answer your questions. When you RSVP we
        keep your contact details and the first and last name of everyone in your party, which go
        on the printed list at the door, plus the card brand and last four digits if you add a
        donation. We do not sell them or share them with anyone other than the hotel and the
        payment processor.
      </p>
      <h2 className={h}>Card data</h2>
      <p className={p}>
        Full card numbers are handled only by MobiusPay&apos;s hosted payment fields, loaded from
        secure.mobiusgateway.com. This site and its servers never see them.
      </p>
      <h2 className={h}>Cookies</h2>
      <p className={p}>
        The public site sets no cookies of its own. The admin area sets one strictly necessary
        sign-in cookie for the family members who manage rooms. Google Analytics cookies are set
        only if you accept them in the notice at the bottom of the page.{' '}
        {readConsent() ? (
          <>
            Your current choice is <strong>{readConsent()}</strong>.{' '}
            <button type="button" className={link} onClick={resetConsent}>
              Change it
            </button>
            .
          </>
        ) : null}
      </p>
      <h2 className={h}>Services we rely on</h2>
      <ul className={`${p} list-disc pl-5`}>
        <li>MobiusPay (payment processing; hosted card fields on the checkout page).</li>
        <li>
          Google reCAPTCHA on the checkout page, to block bots; subject to Google&apos;s privacy
          policy and terms.
        </li>
        <li>Google Fonts, which serves the typefaces and sees your IP address when they load.</li>
        <li>Google Analytics, only with your consent, to see which pages help people.</li>
        <li>
          Sentry error monitoring, which records technical details when something breaks; form
          contents are masked.
        </li>
        <li>
          Amazon Web Services, which hosts the site, and Google Workspace, which relays the
          confirmation emails.
        </li>
      </ul>
      <h2 className={h}>How long we keep it</h2>
      <p className={p}>
        Reservation and RSVP records are kept until the event is settled with the hotel and any
        card disputes are closed, then deleted. Analytics data is aggregated and not tied to your
        name.
      </p>
      <h2 className={h}>Your choices</h2>
      <p className={p}>
        Email us to see, correct or delete what we hold about you. Deleting a reservation record
        before the event means giving up the room, since we would have no way to check you in.
      </p>
      <h2 className={h}>Contact</h2>
      <p className={p}>
        {s?.contactEmail
          ? `Email ${s.contactEmail} with any questions about your information, or use the `
          : 'Use the '}
        <Link to="/contact" className={link}>
          contact page
        </Link>
        .
      </p>
    </Section>
  );
}
